import { Box } from '@mui/system';
import {
  Alert, Backdrop, CircularProgress, Snackbar, Typography, useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller, FieldValues, FormState, useForm,
  UseFormSetValue,
} from 'react-hook-form';
import {
  FunctionComponent,
  SVGProps,
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { TestTypes } from '../../../assets/testTypes';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import {
  selectBusinessInformationQuestions,
  selectMemberInformation, selectMemberSession, selectOnboardingForm, updateFormValidState,
} from '../../../store/slices/onboardingSlice';
import InputComponent from '../../../common/QuestionComponent';
import { Question, QuestionOption } from '../../../common/models/Question';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { ONBOARDING_FORMS } from '../constants';
import { Answer } from '../../../common/models/Answer';
import { useSolutionOrg } from '../../../config/useSolutionOrg';
import { HOME_DEPOT_SETUP_OR_SKIP_ROUTE, HOME_DEPOT_SETUP_WEB } from '../../../routes/namedRoutes';
import { Member } from '../../../common/models/Member';
import { EnrollmentRequest, toEnrolledMember } from '../../../common/models/EnrollmentRequest';
import { enrollMember, getMemberInformation } from '../../../store/thunks/onboardingThunk';
import { ReactComponent as CarpenterIcon } from '../../../assets/images/carpenter.svg';
import { ReactComponent as ForkliftIcon } from '../../../assets/images/forklift.svg';
import { ReactComponent as HandymanIcon } from '../../../assets/images/handyman.svg';
import { ReactComponent as ModeFanIcon } from '../../../assets/images/mode_fan.svg';
import { ReactComponent as OtherTradeIcon } from '../../../assets/images/other_trade.svg';
import { ReactComponent as PlumbingIcon } from '../../../assets/images/plumbing.svg';
import { ReactComponent as PowerIcon } from '../../../assets/images/power.svg';
import { ReactComponent as ToolsPowerDrillIcon }
  from '../../../assets/images/tools_power_drill.svg';
import { ReactComponent as WifiHomeIcon } from '../../../assets/images/wifi_home.svg';
import { useSubmitMarketoData } from '../Marketo/useMarketo';
import { Logger } from '../../../config/logging/logger';
import { EnrollmentResponse } from '../../../common/models/EnrollmentResponse';
import { useOnboardingRedirectPath } from '../../../common/useOnboardingRedirectPath';
import { getCompanyPartnerRelations } from '../../../store/thunks/dashboardThunk';
import { getPartnerEnrollmentStatus, homeDepotPartnerId } from '../../../common/utils/PartnerEnrollmentUtils';
import { PartnerEnrollmentStatus, ShoppingStatus } from '../../../common/enums';
import {
  BUSINESS_TRADE_OPTIONS,
  OTHER_BUSINESS_TRADE,
} from '../../../common/businessTradeOptions';

interface BusinessInputProps {
  question: Question;
  handleControlChange: (question: Question) => void;
  formState: FormState<FieldValues>;
  control: Control<FieldValues, unknown>
}

interface BusinessMainIndustryRadioGroupInputProp {
  question: Question;
  businessMainIndustry: string | undefined;
  control: Control<FieldValues, unknown>
  setShowBusinessTradeAutoComplete: (value: boolean) => void;
  setValue: UseFormSetValue<FieldValues>
}

const TRADES_RADIO_GROUP_CONTROLLER_NAME = 'businessMainIndustryRadioGroup';

const BusinessMainIndustryRadioGroupInput = (
  {
    question,
    businessMainIndustry,
    control,
    setShowBusinessTradeAutoComplete,
    setValue,
  }: BusinessMainIndustryRadioGroupInputProp,
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { trackEdgeEnrollment } = useCustomEventTracking();
  const autocompleteControllerName =
    question?.meta?.memberProperty as TestTypes.Onboarding.QuestionKeys ||
    question?.title;
  const defaultOption = question.options?.find(
    (option: QuestionOption) => option.display === businessMainIndustry,
  );
  const chooseOtherTradeOption = useMemo(() => [
    {
      value: OTHER_BUSINESS_TRADE,
      display: 'Choose other trade',
    },
  ], []);

  const handymanDisplayName = BUSINESS_TRADE_OPTIONS[2];
  const handymanDisplayNameAlias = 'Handyman/ handywo…';
  const hvacDisplayName = BUSINESS_TRADE_OPTIONS[6];
  const hvacDisplayNameAlias = 'HVAC';

  const getDisplayName = (optionDisplayName: string) => {
    if (optionDisplayName === handymanDisplayName) {
      return handymanDisplayNameAlias;
    }
    if (optionDisplayName === hvacDisplayName) {
      return hvacDisplayNameAlias;
    }
    return optionDisplayName;
  };

  const predefinedOptions = useMemo(() => {
    const icons = [
      ToolsPowerDrillIcon,
      ForkliftIcon,
      HandymanIcon,
      WifiHomeIcon,
      PowerIcon,
      CarpenterIcon,
      ModeFanIcon,
      PlumbingIcon,
    ];
    return [
      ...BUSINESS_TRADE_OPTIONS.map((trade, index) => ({
        value: trade,
        display: trade,
        icon: icons[index],
      })),
      {
        value: OTHER_BUSINESS_TRADE,
        display: 'Other',
        icon: OtherTradeIcon,
      },
    ];
  }, []);

  const radioOptions = (defaultOption ? [defaultOption, ...chooseOtherTradeOption] :
    predefinedOptions) as {
    value: string;
    display: string;
    icon?: FunctionComponent<SVGProps<SVGSVGElement>>
  }[];

  const onRadioChange = (value: string | undefined) => {
    setValue(autocompleteControllerName, null);
    if (value === OTHER_BUSINESS_TRADE) {
      setShowBusinessTradeAutoComplete(true);
    } else {
      setShowBusinessTradeAutoComplete(false);
      trackEdgeEnrollment('Change', 'OnboardingQuestionAnswer', {
        questionId: question.id,
        questionTitle: question.title,
        trade: value,
      });
    }
  };

  return (
    <Box mb={theme.spacing(2.5)}>
      <Box>
        <Typography component='div' sx={{
          textAlign: 'left',
          mb: theme.spacing(0.625),
        }}>
          {defaultOption ? t('onboarding.questionPlaceholder.tradeHeading3V2') :
            t('onboarding.questionPlaceholder.tradeHeading4V2')
          }
        </Typography>
      </Box>
      <Controller
        name={TRADES_RADIO_GROUP_CONTROLLER_NAME}
        control={control}
        rules={question?.meta?.rules || {}}
        defaultValue={defaultOption}
        render={(
          {
            field: {
              onChange,
            },
          },
        ) => (
          <RadioGroup
            defaultValue={defaultOption?.value || ''}
            onChange={(event) => {
              const selected = radioOptions.find(
                (opt) => opt.value === event.target.value,
              );
              onChange(selected ? { value: selected.value, display: selected.display } : undefined);
              onRadioChange(selected?.value);
            }}
            sx={{ width: '100%' }}
          >
            <Grid container spacing={{ xs: 1 }} sx={{ ml: `${theme.spacing(0)} !important` }}>
              {radioOptions.map((option) => (
                <Grid key={option.value} item xs={6} md={radioOptions.length === 2 ? 6 : 4}>
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={
                      <Radio
                        sx={{
                          color: theme.palette.grey[300],
                          '&.Mui-checked': {
                            color: theme.palette.brand.primary,
                          },
                          '&:hover': {
                            backgroundColor: 'transparent', // remove hover effect
                          },
                        }}
                      />
                    }
                    label={
                      <Box display={'flex'} alignItems={'center'}>
                        {option.icon &&
                          <Box sx={{
                            width: theme.spacing(3),
                            height: theme.spacing(3),
                            marginRight: theme.spacing(0.75),
                          }}>
                            <option.icon />
                          </Box>
                        }
                        <Box sx={{
                          textAlign: 'left',
                        }}>
                          <Typography variant='body7'>
                            {getDisplayName(option.display)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{
                      width: '100%',
                      alignItems: 'center',
                      marginBottom: 1.5,
                      padding: 1,
                      borderRadius: theme.spacing(1.25),
                      border: `1px solid  ${theme.palette.grey[300]}`,
                      background: theme.palette.common.white,
                      boxSizing: 'border-box',
                      display: 'flex',
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
        )}
      />
    </Box>
  );
};

const BusinessInput = ({
  question,
  handleControlChange,
  formState,
  control,
}: BusinessInputProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const controllerName =
    question?.meta?.memberProperty as TestTypes.Onboarding.QuestionKeys ||
    question?.title;
  return (
    <Box mb={theme.spacing(2.5)}>
      <Typography sx={{
        textAlign: 'left',
        mb: theme.spacing(0.625),
      }}>
        {question?.title === 'Business or trade' ?
          t(`onboarding.questionPlaceholder.${'Business or trade variant 2'}`) :
          t('onboarding.questionPlaceholder.topBusinessPriorityV2')}
      </Typography>
      <Controller
        name={controllerName}
        control={control}
        rules={question?.meta?.rules || {}}
        render={(
          {
            field: {
              onChange, value, onBlur,
            },
          },
        ) => (
          <Box>
            <InputComponent
              type={question?.type as string}
              onChange={(e) => {
                onChange(e); // Ensure React Hook Form's internal state is updated
                handleControlChange(question as Question);
              }}
              inputValue={value}
              meta={question?.meta}
              options={question?.options}
              // eslint-disable-next-line no-nested-ternary
              placeholderText={question?.title ? (
                question.title === 'Business or trade' ?
                  'businessOrTradePlaceholderV2' :
                  question.title
              ) : ''}
              subTitle={t(`onboarding.questionPlaceholder.${question?.meta?.subTitle}`) as string}
              onBlur={onBlur}
              showAutoCompletePlaceholderText={true}
              calledFromOnboarding
              data-testid={controllerName}
              optionalRadioBtnSelectRadioStyles={{
                color: theme.palette.grey[300],
                '&.Mui-checked': {
                  color: theme.palette.brand.primary,
                },
                '&:hover': {
                  backgroundColor: 'transparent', // remove hover effect
                },
              }}
              optionalRadioBtnSelectFormLabelStyles={{
                borderRadius: theme.spacing(1.25),
                border: `1px solid  ${theme.palette.grey[300]}`,
                background: theme.palette.common.white,
              }}
            />
          </Box>
        )
        }
      />
    </Box>
  );
};

export function OnboardBusinessInformationWrapperV2() {
  const { t } = useTranslation();
  const { trackEdgeEnrollment, trackRequest } = useCustomEventTracking();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [showBusinessTradeAutoComplete, setShowBusinessTradeAutoComplete] = useState(false);
  const {
    isNativeAppExperience,
    envSolutionOrgId,
    envEnableMarketoDataSubmission,
  } = useSolutionOrg();
  const onboardingForm = useAppSelector(selectOnboardingForm);
  const memberSession = useAppSelector(selectMemberSession);
  const businessGoalsTitle = 'What’s most important to your business?*';
  const businessMainIndustryTitle = 'Business or trade';
  const businessInformationQuestions = useAppSelector(selectBusinessInformationQuestions);
  const memberInformation = useAppSelector(selectMemberInformation);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const submitMarketoData = useSubmitMarketoData();
  const logger = Logger.create('OnboardBusinessInformationWrapperV2');
  const businessGoalsQuestion = businessInformationQuestions?.find(
    (question) => question.title === businessGoalsTitle,
  );
  const businessMainIndustryQuestion = businessInformationQuestions?.find(
    (question) => question.title === businessMainIndustryTitle,
  );

  const businessGoalsControllerName = businessGoalsTitle;
  const businessMainIndustryControllerName =
    businessMainIndustryQuestion?.meta?.memberProperty as string;

  const {
    handleSubmit,
    control,
    formState,
    setValue,
    trigger,
    getValues,
  } = useForm({ mode: 'onChange' });

  useEffect(() => {
    dispatch(updateFormValidState(formState.isValid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  // get onboarding redirect for post HD enrollment
  const redirectPathForPostHDEnrollment = useOnboardingRedirectPath({
    redirectToPartnersForOnboardingFlowTestOff: true,
  });

  const navigateToHomeDepot = () => {
    if (isNativeAppExperience) {
      // move to the home depot splash screen
      navigate(HOME_DEPOT_SETUP_OR_SKIP_ROUTE);
    } else {
      // navigate to home depot setup for web
      navigate(HOME_DEPOT_SETUP_WEB);
    }
  };

  const handleControlChange = useCallback((question: Question) => {
    trackEdgeEnrollment('Change', 'OnboardingQuestionAnswer', {
      questionId: question.id,
      questionTitle: question.title,
      trade: getValues(businessMainIndustryControllerName)?.value ||
       getValues(TRADES_RADIO_GROUP_CONTROLLER_NAME)?.value,
    });
  }, [trackEdgeEnrollment, businessMainIndustryControllerName, getValues]);

  const handleClose = () => {
    setOpenErrorSnackbar(false);
    setOpenSuccessSnackbar(false);
  };

  const prepareEnrollmentRequest = async (memberInfo: Member, formAnswers: Answer[]) => {
    const request: EnrollmentRequest = {
      memberId: memberInfo.id,
      member: toEnrolledMember(memberInfo),
      forms: [
        {
          formId: onboardingForm.id,
          answers: formAnswers,
        },
      ],
      partners: [], // Do not auto-enroll partners during edge enrollment
    };

    const solutionOrgId = memberSession.solutionOrgId || (envSolutionOrgId as string) || '';

    try {
      const enrollmentResponse = await dispatch(
        enrollMember({ solutionOrgId, request, trackRequest }),
      ).then(unwrapResult);
      setOpenSuccessSnackbar(true);
      setOpenErrorSnackbar(false);

      // Re-fetch member info so Redux (and the Chameleon identify() call that reads
      // edgeEnrollmentDate from it) has the freshly-set enrollment date before we
      // navigate away, instead of racing the next page's own refetch. Best-effort:
      // enrollment already succeeded, so a refresh failure shouldn't block navigation.
      await dispatch(
        getMemberInformation({ trackRequest }),
      ).then(unwrapResult).catch(() => undefined);

      if ((enrollmentResponse as EnrollmentResponse).isFirstEdgeEnrollmentInCompany) {
        // send a rewards everpro signup completed event
        trackEdgeEnrollment('Submit', 'Signup', { isOptimizelyEvent: true });

        navigateToHomeDepot();

        // send marketo data
        if (envEnableMarketoDataSubmission) {
          submitMarketoData(memberSession.memberEmail);
        }
      } else {
        const companyPartnerRelations = await dispatch(getCompanyPartnerRelations(
          (enrollmentResponse as EnrollmentResponse).companyId,
        )).then(unwrapResult);

        const homeDepotCompanyRelation = companyPartnerRelations.find((cp) => (
          cp.partnerId && cp.partnerId === homeDepotPartnerId
        ));
        const homeDepotEnrollmentStatus = getPartnerEnrollmentStatus(
          homeDepotCompanyRelation,
        );

        if (homeDepotEnrollmentStatus === PartnerEnrollmentStatus.Completed ||
          homeDepotCompanyRelation?.shoppingStatus === ShoppingStatus.NotShopping
        ) {
          navigate(redirectPathForPostHDEnrollment);
        } else {
          navigateToHomeDepot();
        }
      }
    } catch (error: unknown) {
      setOpenSuccessSnackbar(false);
      setOpenErrorSnackbar(true);
      setLoading(false);
    }
  };

  const onSubmit = (data: FieldValues) => {
    logger.debug(Logger.format(`data: ${JSON.stringify(data)}`));
    if (data) {
      const updatedMemberInfo: { [key: string]: unknown } = { ...memberInformation };
      const answers: Answer[] = [];

      // business main industry
      const businessMainIndustryProp = businessMainIndustryQuestion?.meta?.memberProperty;
      const propValue = (
        (data[businessMainIndustryControllerName] ||
          data[TRADES_RADIO_GROUP_CONTROLLER_NAME]) as QuestionOption
      )?.value;
      updatedMemberInfo[businessMainIndustryProp as keyof Member] = propValue || '';

      // business goals
      const answer: Answer = {
        questionId: businessGoalsQuestion?.id || '',
        value: JSON.stringify(data[businessGoalsControllerName]),
      };
      answers.push(answer);
      setLoading(true);
      prepareEnrollmentRequest(updatedMemberInfo as unknown as Member, answers);
    }
  };

  return (
    <Box mt={theme.spacing(2.5)}>
      <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center'
        sx={{
          mb: theme.spacing(2.5),
        }}
      >
        <Typography component='div' variant='h12' sx={{ textAlign: 'center' }}>
          {t('onboarding.questionPlaceholder.tradeHeading1V2')}
        </Typography>
        <Typography component='div' sx={{
          textAlign: 'center',
          mt: theme.spacing(0.5),
          maxWidth: '100%',
        }}>
          {t('onboarding.questionPlaceholder.tradeHeading2V2')}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Box>
          <Box
            sx={{
              width: '100%',
            }}
          >
            <form id={ONBOARDING_FORMS.businessInformation.id} onSubmit={handleSubmit(onSubmit)}>
              <Snackbar
                open={openSuccessSnackbar}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Alert severity="success" sx={{ width: '100%' }}>
                  {t('success.generalMessage')}
                </Alert>
              </Snackbar>
              <Snackbar
                open={openErrorSnackbar}
                autoHideDuration={4000}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Alert severity="error" sx={{ width: '100%' }}>
                  {t('error.generalMessage')}
                </Alert>
              </Snackbar>
              <Backdrop
                sx={{
                  color: theme.palette.common.white,
                  zIndex: () => theme.zIndex.drawer + 1,
                }}
                open={loading}>
                <CircularProgress color="inherit" />
              </Backdrop>
              <Box>
                {businessMainIndustryQuestion && memberInformation.id &&
                  <BusinessMainIndustryRadioGroupInput
                    question={businessMainIndustryQuestion as Question}
                    control={control}
                    businessMainIndustry={memberInformation.businessMainIndustry}
                    setShowBusinessTradeAutoComplete={setShowBusinessTradeAutoComplete}
                    setValue={setValue}
                  />}
                {showBusinessTradeAutoComplete && businessMainIndustryQuestion &&
                  <BusinessInput
                    question={businessMainIndustryQuestion as Question}
                    handleControlChange={handleControlChange}
                    formState={formState}
                    control={control}
                  />}
                {businessGoalsQuestion && <BusinessInput
                  question={businessGoalsQuestion as Question}
                  handleControlChange={handleControlChange}
                  formState={formState}
                  control={control}
                />}
              </Box>
            </form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
